import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, BadgeCheck } from "lucide-react";
import { SITE, PARTNERS, REGIONAL_PARTNERS } from "@/data/site";
import { KEY_SERVICES, TUNING_SERVICES } from "@/data/services";
import Logo from "@/components/Logo";

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-[#050505]" data-testid="footer">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="mb-5 inline-flex">
              <Logo />
            </Link>
            <p className="font-body text-sm text-neutral-400 leading-relaxed">
              Dein mobiler Spezialist für Chiptuning, KFZ-Elektronik und
              Autoschlüssel im Raum {SITE.address.city} & Oldenburger Münsterland.
            </p>
            <div className="mt-6">
              <p className="mb-3 flex items-center gap-2 font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt">
                <BadgeCheck className="h-4 w-4" /> Partner & Technik
              </p>
              <ul className="space-y-1.5">
                {PARTNERS.map((p) => (
                  <li key={p.name}>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener"
                      data-testid={`footer-partner-${p.name.toLowerCase()}`}
                      className="font-body text-sm text-neutral-400 transition-colors hover:text-volt"
                    >
                      {p.anchor}
                    </a>
                  </li>
                ))}
                {REGIONAL_PARTNERS.map((r) => (
                  <li key={r.url}>
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener"
                      data-testid={`footer-regional-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="font-body text-sm text-neutral-400 transition-colors hover:text-volt"
                    >
                      {r.anchor}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <p className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt mb-4">
              Tuning & Elektronik
            </p>
            <ul className="space-y-2">
              {TUNING_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="font-body text-sm text-neutral-400 hover:text-white transition-colors">
                    {s.nav}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt mb-4">
              Schlüssel & Notdienst
            </p>
            <ul className="space-y-2">
              {KEY_SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="font-body text-sm text-neutral-400 hover:text-white transition-colors">
                    {s.nav}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-volt mb-4">
              Kontakt
            </p>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${SITE.phoneIntl}`} className="flex items-start gap-3 font-body text-sm text-neutral-300 hover:text-volt transition-colors">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-volt" /> {SITE.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 font-body text-sm text-neutral-300 hover:text-volt transition-colors">
                  <MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-volt" /> WhatsApp
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE.email}`} className="flex items-start gap-3 font-body text-sm text-neutral-300 hover:text-volt transition-colors">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-volt" /> {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3 font-body text-sm text-neutral-400">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-volt" />
                <span>{SITE.address.street}<br />{SITE.address.zip} {SITE.address.city}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-body text-xs text-neutral-500">
            © {new Date().getFullYear()} {SITE.legalName}. Alle Rechte vorbehalten.{" "}
            <span className="text-neutral-600">·</span>{" "}
            made by{" "}
            <a
              href="https://filesuite.app"
              target="_blank"
              rel="noopener"
              data-testid="footer-madeby-filesuite"
              className="font-semibold text-neutral-400 transition-colors hover:text-volt"
            >
              FileSuite
            </a>
          </p>
          <div className="flex items-center gap-6">
            <Link to="/impressum" data-testid="footer-impressum" className="font-body text-xs text-neutral-500 hover:text-white transition-colors">
              Impressum
            </Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz" className="font-body text-xs text-neutral-500 hover:text-white transition-colors">
              Datenschutz
            </Link>
            <button
              type="button"
              data-testid="footer-cookie-settings"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="font-body text-xs text-neutral-500 hover:text-white transition-colors"
            >
              Cookie-Einstellungen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
